import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { canonicalOrderStatus, formatMoney, humanizeStatus } from "../lib/safeData";
import {
  Package,
  ArrowLeft,
  Calendar,
  Truck,
  MapPin,
  CheckCircle2,
  Clock,
  CreditCard,
} from "lucide-react";

const statusColors = {
  pending_payment: "status-pending",
  paid: "status-paid",
  processing: "status-processing",
  shipped: "status-shipped",
  delivered: "status-delivered",
  cancelled: "status-cancelled",
};

const statusSteps = [
  { key: "paid", label: "Paid", icon: CreditCard },
  { key: "processing", label: "Processing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        withCredentials: true,
      });
      setOrder(response.data);
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Failed to load order");
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price, currency) => formatMoney(price, currency);

  const getStatusStepIndex = (status) => {
    const statusOrder = ["pending_payment", "paid", "processing", "shipped", "delivered"];
    // Orders are stored with the backend lifecycle spellings (UNPAID,
    // PAID_LOCKED, FULFILLED), so canonicalise before locating the step.
    return statusOrder.indexOf(canonicalOrderStatus(status));
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Order not found
          </h2>
          <Button onClick={() => navigate("/orders")}>View All Orders</Button>
        </div>
      </Layout>
    );
  }

  const currentStepIndex = getStatusStepIndex(order.status);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/orders")}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>

        {/* Order Header */}
        <div className="container-card p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="order-title">
                Order #{order.order_id}
              </h1>
              <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                <Calendar className="h-4 w-4" />
                {formatDate(order.created_at)}
              </div>
            </div>
            <Badge
              className={`${statusColors[canonicalOrderStatus(order.status)] || "bg-slate-200"} text-sm px-4 py-2`}
            >
              {humanizeStatus(order.status)}
            </Badge>
          </div>

          {/* Status Progress */}
          {!["cancelled", "pending_payment"].includes(canonicalOrderStatus(order.status)) && (
            <div className="relative">
              <div className="flex justify-between">
                {statusSteps.map((step, idx) => {
                  const isCompleted = idx <= currentStepIndex - 1;
                  const isCurrent = idx === currentStepIndex - 1;
                  return (
                    <div key={step.key} className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isCompleted
                            ? "bg-green-500 text-white"
                            : isCurrent
                            ? "bg-orange-500 text-white"
                            : "bg-slate-200 text-slate-500"
                        }`}
                      >
                        <step.icon className="h-5 w-5" />
                      </div>
                      <span className="text-xs mt-2 text-slate-600">{step.label}</span>
                    </div>
                  );
                })}
              </div>
              {/* Progress Line */}
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 -z-10 mx-12">
                <div
                  className="h-full bg-green-500 transition-all"
                  style={{ width: `${Math.max(0, (currentStepIndex - 1) / 3) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Order Items */}
          <div className="container-card p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">
              Order Items
            </h2>
            <div className="space-y-4">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-slate-100 rounded-sm overflow-hidden flex-shrink-0">
                    {item.images?.[0] ? (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {item.name}
                    </p>
                    <p className="text-sm text-slate-500">
                      Qty: {item.quantity} × {formatPrice(item.locked_price, order.currency)}
                    </p>
                  </div>
                  <p className="font-medium text-slate-900">
                    {formatPrice(item.item_total, order.currency)}
                  </p>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span>{formatPrice(order.total, order.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Shipping ({order.shipping_speed})</span>
                <span>Included</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Customs & Duties</span>
                <span>Included</span>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span className="landed-price" data-testid="order-total">
                {formatPrice(order.total, order.currency)}
              </span>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="container-card p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">
              <MapPin className="inline h-5 w-5 mr-2" />
              Shipping Address
            </h2>
            {order.shipping_address && (
              <div className="text-slate-600 space-y-1">
                <p className="font-medium text-slate-900">
                  {order.shipping_address.name}
                </p>
                <p>{order.shipping_address.street}</p>
                <p>
                  {order.shipping_address.city}
                  {order.shipping_address.state && `, ${order.shipping_address.state}`}
                </p>
                <p>{order.shipping_address.postal_code}</p>
                <p>{order.shipping_address.country}</p>
                <Separator className="my-4" />
                <p className="text-sm">
                  <span className="text-slate-500">Phone:</span>{" "}
                  {order.shipping_address.phone}
                </p>
                <p className="text-sm">
                  <span className="text-slate-500">Email:</span>{" "}
                  {order.shipping_address.email}
                </p>
              </div>
            )}

            <Separator className="my-4" />

            <h3 className="font-medium text-slate-900 mb-2">
              <Truck className="inline h-4 w-4 mr-2" />
              Delivery Details
            </h3>
            <p className="text-sm text-slate-600">
              <span className="capitalize">{order.shipping_speed}</span> shipping to{" "}
              {order.destination}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {order.shipping_speed === "express"
                ? "Estimated delivery: 3-5 business days"
                : "Estimated delivery: 7-14 business days"}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
